import { Routes, Route, Navigate } from 'react-router-dom';
import { useMe } from './hooks/useAuth.js';
import { Layout } from './components/Layout.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { ApiError } from './api/index.js';
import { Login } from './pages/Login.js';
import { Dashboard } from './pages/Dashboard.js';
import { Upload } from './pages/Upload.js';
import { BatchList } from './pages/BatchList.js';
import { BatchDetail } from './pages/BatchDetail.js';
import { Catalog } from './pages/Catalog.js';
import { ItemDetail } from './pages/ItemDetail.js';
import { ItemEdit } from './pages/ItemEdit.js';
import CsvMapper from './pages/CsvMapper.js';
import { SeriesList } from './pages/SeriesList.js';
import { SeriesDetail } from './pages/SeriesDetail.js';
import { GroupList } from './pages/GroupList.js';
import { GroupDetail } from './pages/GroupDetail.js';
import { ContributorList } from './pages/ContributorList.js';
import { ContributorDetail } from './pages/ContributorDetail.js';
import { PublisherList } from './pages/PublisherList.js';
import { PublisherDetail } from './pages/PublisherDetail.js';
import { GenreList } from './pages/GenreList.js';
import { GenreDetail } from './pages/GenreDetail.js';
import { Redemptions } from './pages/Redemptions.js';
import { Coupons } from './pages/Coupons.js';
import { Loader2 } from 'lucide-react';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { data, isLoading, error } = useMe();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-400">
        <Loader2 className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  // Only redirect to login on actual 401 (unauthorized), not network blips
  const is401 = error instanceof ApiError && error.status === 401;
  if (is401 || (!error && !data)) {
    return <Navigate to="/login" replace />;
  }

  // Non-401 error (network failure, 500, etc.) — show recoverable error instead of broken shell
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4 text-gray-500">
        <p className="text-sm">Connection error. Please refresh the page.</p>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-brand-600 underline"
        >
          Refresh
        </button>
      </div>
    );
  }

  return <Layout>{children}</Layout>;
}

export default function App() {
  return (
    <ErrorBoundary label="App">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <ErrorBoundary label="Dashboard">
                <Dashboard />
              </ErrorBoundary>
            </RequireAuth>
          }
        />
        <Route
          path="/upload"
          element={
            <RequireAuth>
              <ErrorBoundary label="Upload">
                <Upload />
              </ErrorBoundary>
            </RequireAuth>
          }
        />
        <Route
          path="/batches"
          element={
            <RequireAuth>
              <ErrorBoundary label="BatchList">
                <BatchList />
              </ErrorBoundary>
            </RequireAuth>
          }
        />
        <Route
          path="/batches/:id"
          element={
            <RequireAuth>
              <ErrorBoundary label="BatchDetail">
                <BatchDetail />
              </ErrorBoundary>
            </RequireAuth>
          }
        />
        <Route
          path="/catalog"
          element={
            <RequireAuth>
              <ErrorBoundary label="Catalog">
                <Catalog />
              </ErrorBoundary>
            </RequireAuth>
          }
        />
        <Route
          path="/catalog/:id"
          element={
            <RequireAuth>
              <ErrorBoundary label="ItemDetail">
                <ItemDetail />
              </ErrorBoundary>
            </RequireAuth>
          }
        />
        <Route
          path="/catalog/:id/edit"
          element={
            <RequireAuth>
              <ErrorBoundary label="ItemEdit">
                <ItemEdit />
              </ErrorBoundary>
            </RequireAuth>
          }
        />
        <Route
          path="/batches/:batchId/map"
          element={
            <RequireAuth>
              <ErrorBoundary label="CsvMapper">
                <CsvMapper />
              </ErrorBoundary>
            </RequireAuth>
          }
        />
        <Route
          path="/series"
          element={<RequireAuth><ErrorBoundary label="SeriesList"><SeriesList /></ErrorBoundary></RequireAuth>}
        />
        <Route
          path="/series/:id"
          element={<RequireAuth><ErrorBoundary label="SeriesDetail"><SeriesDetail /></ErrorBoundary></RequireAuth>}
        />
        <Route
          path="/groups"
          element={<RequireAuth><ErrorBoundary label="GroupList"><GroupList /></ErrorBoundary></RequireAuth>}
        />
        <Route
          path="/groups/:id"
          element={<RequireAuth><ErrorBoundary label="GroupDetail"><GroupDetail /></ErrorBoundary></RequireAuth>}
        />
        <Route
          path="/contributors"
          element={<RequireAuth><ErrorBoundary label="ContributorList"><ContributorList /></ErrorBoundary></RequireAuth>}
        />
        <Route
          path="/contributors/:id"
          element={<RequireAuth><ErrorBoundary label="ContributorDetail"><ContributorDetail /></ErrorBoundary></RequireAuth>}
        />
        <Route
          path="/publishers"
          element={<RequireAuth><ErrorBoundary label="PublisherList"><PublisherList /></ErrorBoundary></RequireAuth>}
        />
        <Route
          path="/publishers/:id"
          element={<RequireAuth><ErrorBoundary label="PublisherDetail"><PublisherDetail /></ErrorBoundary></RequireAuth>}
        />
        <Route
          path="/genres"
          element={<RequireAuth><ErrorBoundary label="GenreList"><GenreList /></ErrorBoundary></RequireAuth>}
        />
        <Route
          path="/genres/:id"
          element={<RequireAuth><ErrorBoundary label="GenreDetail"><GenreDetail /></ErrorBoundary></RequireAuth>}
        />
        <Route
          path="/redemptions"
          element={<RequireAuth><ErrorBoundary label="Redemptions"><Redemptions /></ErrorBoundary></RequireAuth>}
        />
        <Route
          path="/coupons"
          element={<RequireAuth><ErrorBoundary label="Coupons"><Coupons /></ErrorBoundary></RequireAuth>}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}
